import { useCallback, useEffect, useState } from 'react';
import type { MiPerfil, OperacionesClienteFinal } from '../api/ops/clienteFinal';
import { partesDelQr, type CuandoMisPases, type ListaMisPases, type MiPase } from '../api/ops/pasesCliente';
import { ErrorApi } from '../api/problema';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import { Menu } from '../componentes/Menu';
import type { NombreIcono } from '../componentes/iconos';
import { navegador } from '../logica/navegador';
import { haySiguiente, usePaginacionCursor } from '../logica/paginacion';
import { useCarteras, type Nota } from './carteras';
import { BloqueQr, BotonesCartera, NotaPase } from './PaseComun';
import { BotonPc, Pagina } from './Pagina';
import { agruparPorAcceso, chipDelPase, codigoEnGrupos, cuando, fechaAnterior, paseNDeN, REENVIO_SIN_CONTACTO, sePuedeUsar, textoReenviado, ventana } from './pases';

/**
 * «Mis pases» (6.7, `/mis-pases`): con el token de cliente de «Mis datos» (2.9), el negocio a elegir entre sus perfiles
 * (`listMyBusinessProfiles`) y sus pases ahí (`listMyPasses`): «Próximos» y «Pasados», con cursor (G121). El primero de
 * los próximos, «Tu próximo pase», con su QR a la vista y «Abrir el pase» (su página, la dirección del QR); los demás se
 * abren con `getMyPass`. «Reenviármelo» (`resendMyPass`, 5 por hora), las carteras (6.8) y el chip de cada estado. Los del
 * mismo acceso (`access_group_id` o, sin grupo, `passable_type` + `passable_id`) van juntos bajo su nombre, «Sáb 17 oct ·
 * 2 pases», y cada fila dice de quién es y «Pase n de N» (G785, `agruparPorAcceso`).
 */
interface Props {
    ops: OperacionesClienteFinal;
    onSalir: () => void;
    onCaducada: () => void;
}

type Lista = { clave: string; datos: ListaMisPases | null; error: boolean };

const fechaHora = (iso: string, zona: string) => {
    const f = new Date(iso);
    try {
        return f.toLocaleString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: zona });
    } catch {
        return f.toLocaleString('es-ES');
    }
};

export function MisPases({ ops, onSalir, onCaducada }: Props) {
    const [perfiles, setPerfiles] = useState<MiPerfil[] | null>(null);
    const [error, setError] = useState(false);
    const [elegido, setElegido] = useState<string | null>(null);
    const [cuandoVer, setCuandoVer] = useState<CuandoMisPases>('upcoming');
    const [lista, setLista] = useState<Lista | null>(null);
    const [abierto, setAbierto] = useState<string | null>(null);
    const [intento, setIntento] = useState(0);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;

    const tratar = useCallback(
        (e: unknown, si: () => void) => {
            if (e instanceof ErrorApi && e.estado === 401) {
                onCaducada();
            } else {
                si();
            }
        },
        [onCaducada],
    );

    useEffect(() => {
        let vivo = true;
        ops.listMyBusinessProfiles({ limit: 100 })
            .then((l) => {
                if (vivo) {
                    setPerfiles(l.data.filter((p) => p.ended == null));
                    setError(false);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    tratar(e, () => { setError(true); });
                }
            });
        return () => {
            vivo = false;
        };
        // tratar cambia con onCaducada en cada pintado: solo se mira al cargar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, intento]);

    const perfil = perfiles?.find((p) => p.customer_id === elegido) ?? perfiles?.[0] ?? null;
    const idPerfil = perfil?.customer_id ?? null;
    const clave = `${idPerfil ?? ''}|${cuandoVer}|${pag.cursor ?? ''}|${String(intento)}`;
    useEffect(() => {
        if (idPerfil === null) {
            return;
        }
        let vivo = true;
        ops.listMyPasses(idPerfil, cuandoVer, { cursor: pag.cursor, limit: 20 })
            .then((datos) => {
                if (vivo) {
                    setLista({ clave, datos, error: false });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    tratar(e, () => { setLista({ clave, datos: null, error: true }); });
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, idPerfil, cuandoVer, pag.cursor, clave]);

    const nombre = perfil?.business.name ?? '';
    const actual = lista?.clave === clave ? lista : null;
    const pases = actual?.datos?.data ?? null;
    // G987, G1025: el avatar con el color de marca del negocio (`MyProfile.business.brand.primary_color`, el mismo de la
    // página del pase, 6.6), también sin pases cargados; sin color, el de Reservaria.
    const color = perfil?.business.brand?.primary_color ?? null;
    const cabecera = perfil === null ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, linea: 'Mis pases', color } as const);
    const pie = perfil === null ? ({ tipo: 'cuenta' } as const) : ({ tipo: 'negocio', nombre, privacidad: perfil.business.privacy_url ?? null } as const);
    const reintentar = () => { setIntento((i) => i + 1); };

    if (error) {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'cuenta' }} onSalir={onSalir} centrada>
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>No hemos podido cargar tus pases</h1><span>Comprueba la conexión y prueba de nuevo.</span></div>
                    <BotonPc texto="Reintentar" icono="refresh-cw" onClick={reintentar} />
                </section>
            </Pagina>
        );
    }

    const cargando = perfiles === null || (perfil !== null && actual === null);
    const primero = cuandoVer === 'upcoming' && !pag.hayAnterior && pases !== null && pases.length > 0 && pases[0] !== undefined && sePuedeUsar(pases[0].status) ? pases[0] : null;
    const resto = primero === null ? (pases ?? []) : (pases ?? []).slice(1);

    return (
        <Pagina
            cabecera={cabecera}
            pie={pie}
            onSalir={onSalir}
            titulo={{ titulo: 'Mis pases', sub: nombre === '' ? 'Tus pases con QR: ábrelos en la puerta.' : `Tus pases con QR en ${nombre}: ábrelos en la puerta.` }}
        >
            {perfiles !== null && perfiles.length > 1 && (
                <label className="rv-pc-negocio__canal">
                    <span>Negocio</span>
                    <select
                        className="rv-pc-select rv-pc-select--ancho"
                        value={perfil?.customer_id ?? ''}
                        onChange={(e) => {
                            setElegido(e.target.value);
                            setAbierto(null);
                            reiniciar();
                        }}
                    >
                        {perfiles.map((p) => (
                            <option key={p.customer_id} value={p.customer_id}>{p.business.name}</option>
                        ))}
                    </select>
                </label>
            )}
            <div className="rv-pc-tipos rv-ps-cuando" role="radiogroup" aria-label="Qué pases ver">
                {(
                    [
                        ['upcoming', 'Próximos', 'calendar'],
                        ['past', 'Pasados', 'history'],
                    ] as [CuandoMisPases, string, NombreIcono][]
                ).map(([k, t, icono]) => (
                    <button
                        key={k}
                        type="button"
                        role="radio"
                        aria-checked={cuandoVer === k}
                        className="rv-pc-tipo"
                        onClick={() => {
                            if (cuandoVer !== k) {
                                setCuandoVer(k);
                                setAbierto(null);
                                reiniciar();
                            }
                        }}
                    >
                        <Icono nombre={icono} />
                        {t}
                    </button>
                ))}
            </div>
            {primero !== null && perfil !== null && <ProximoPase key={primero.id} ops={ops} perfil={perfil.customer_id} pase={primero} onCaducada={onCaducada} />}
            {(cargando || actual?.error === true || resto.length > 0 || primero === null) && (
                <section className="rv-pc-card rv-pc-card--seccion" aria-label={`${cuandoVer === 'upcoming' ? 'Pases próximos' : 'Pases pasados'}${nombre === '' ? '' : ` en ${nombre}`}`} aria-busy={cargando}>
                    {primero !== null && resto.length > 0 && <span className="rv-pc-ceja rv-ps-mas">Más adelante</span>}
                    {cargando ? (
                        <>
                            <div className="rv-pc-esqueleto" style={{ height: 56 }} />
                            <div className="rv-pc-esqueleto" style={{ height: 56, marginTop: 12 }} />
                        </>
                    ) : actual?.error === true ? (
                        <div className="rv-pc-vacio">
                            <span><Icono nombre="circle-alert" /></span>
                            <strong>No hemos podido cargar tus pases</strong>
                            <BotonPc texto="Reintentar" icono="refresh-cw" tipo="sec" peq onClick={reintentar} />
                        </div>
                    ) : resto.length === 0 ? (
                        <div className="rv-pc-vacio">
                            <span><Icono nombre="ticket" /></span>
                            <strong>{cuandoVer === 'upcoming' ? `No tienes pases próximos${nombre === '' ? '' : ` en ${nombre}`}` : `Todavía no tienes pases pasados${nombre === '' ? '' : ` en ${nombre}`}`}</strong>
                            <small>{cuandoVer === 'upcoming' ? 'Cuando compres entradas, reserves o pidas cita con QR, te llegará el pase y lo verás también aquí.' : 'Aquí verás las entradas, reservas y citas que ya usaste o que caducaron.'}</small>
                        </div>
                    ) : (
                        perfil !== null &&
                        agruparPorAcceso(resto).map(({ pase: p, cabecera: cuantos, enGrupo }) => (
                            <FilaPase
                                key={p.id}
                                ops={ops}
                                perfil={perfil.customer_id}
                                pase={p}
                                cabecera={cuantos}
                                enGrupo={enGrupo}
                                abierto={abierto === p.id}
                                onAbrir={() => { setAbierto((a) => (a === p.id ? null : p.id)); }}
                                onCaducada={onCaducada}
                            />
                        ))
                    )}
                </section>
            )}
            {actual?.datos != null && (pag.hayAnterior || haySiguiente(actual.datos.next_cursor)) && (
                <div className="rv-pc-paginas">
                    <BotonPc texto="Anterior" icono="arrow-left" tipo="sec" peq desactivado={!pag.hayAnterior} onClick={() => { setAbierto(null); pag.anterior(); }} />
                    <BotonPc texto="Siguiente" icono="arrow-right" tipo="sec" peq desactivado={!haySiguiente(actual.datos.next_cursor)} onClick={() => { setAbierto(null); pag.siguiente(actual.datos?.next_cursor ?? null); }} />
                </div>
            )}
            {nombre !== '' && <p className="rv-pc-nota rv-final-osc"><Icono nombre="users" /><span>{`Aquí están las entradas, reservas y citas con QR que tienes en ${nombre}. Las de tus acompañantes también, si las compraste tú.`}</span></p>}
        </Pagina>
    );
}

/** «Reenviármelo» de un pase (`resendMyPass`) con su nota: va por el canal por el que se mandó el pase (G927, G954). */
function useReenvio(ops: OperacionesClienteFinal, perfil: string, pase: string, onCaducada: () => void) {
    const [ocupado, setOcupado] = useState(false);
    const [nota, setNota] = useState<Nota | null>(null);
    const reenviar = () => {
        setOcupado(true);
        setNota(null);
        ops.resendMyPass(perfil, pase)
            .then((r) => {
                setNota(['circle-check', 'info', textoReenviado(r.channel, r.destination_hint, r.remaining_this_hour)]);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                } else if (e instanceof ErrorApi && e.estado === 429) {
                    setNota(['clock', 'espera', 'Ya te lo hemos mandado 5 veces en la última hora. Prueba más tarde.']);
                } else if (e instanceof ErrorApi && e.estado === 409) {
                    // G1080: sin contacto (`no_contact`) o pase anulado o caducado (`conflict`).
                    setNota(e.codigo === 'no_contact' ? ['user-x', 'error', REENVIO_SIN_CONTACTO] : ['ban', 'error', 'No hemos podido reenviar este pase: ya no vale.']);
                } else {
                    setNota(['circle-alert', 'error', 'No hemos podido reenviarlo. Comprueba la conexión y prueba de nuevo.']);
                }
            })
            .finally(() => {
                setOcupado(false);
            });
    };
    return { ocupado, nota, setNota, reenviar };
}

const etiquetaDe = (p: MiPase) => p.door_display?.label ?? null;

function CambioDeFecha({ pase }: { pase: MiPase }) {
    const antes = fechaAnterior(pase);
    if (antes === null || pase.status !== 'active') {
        return null;
    }
    return (
        <p className="rv-ps-nota rv-ps-nota--espera">
            <Icono nombre="calendar-clock" />
            <span>
                <span className="rv-ps-antes">Antes:</span> <s>{antes}</s>. Tu QR sigue valiendo.
            </span>
        </p>
    );
}

function ProximoPase({ ops, perfil, pase: p, onCaducada }: { ops: OperacionesClienteFinal; perfil: string; pase: MiPase; onCaducada: () => void }) {
    const { ocupado, nota, setNota, reenviar } = useReenvio(ops, perfil, p.id, onCaducada);
    const { ocupada, anadir } = useCarteras(ops, setNota);
    const partes = partesDelQr(p.qr_url);
    const f = cuando(p);
    const etiqueta = etiquetaDe(p);
    const chip = chipDelPase(p.status, fechaAnterior(p) !== null);
    const linea2 = [`Puedes entrar ${ventana(p).replace(/^De/, 'de')}`, p.holder_name == null ? '' : `A nombre de ${p.holder_name}`].filter((x) => x !== '').join(' · ');
    const opciones: [string, NombreIcono, () => void, boolean][] = [
        ['Reenviármelo', 'send', reenviar, true],
        ['Añadir a Cartera de Apple', 'wallet', () => { if (partes !== null) anadir('apple', partes.negocio, partes.token); }, partes !== null],
        ['Añadir a Google Wallet', 'wallet', () => { if (partes !== null) anadir('google', partes.negocio, partes.token); }, partes !== null],
    ];
    return (
        <section className="rv-pc-card rv-ps-proximo" aria-label="Tu próximo pase">
            <div className="rv-ps-pase__cab">
                <span className="rv-pc-ceja">Tu próximo pase</span>
                {chip !== null && <Chip estado={chip[1]}>{chip[0]}</Chip>}
                {/* G996: el «⋯» es la pieza común RV Menu. */}
                <Menu etiqueta="Más opciones del pase" fantasma opciones={opciones.filter((o) => o[3]).map(([t, icono, accion]) => ({ clave: t, texto: t, icono, desactivada: ocupado || ocupada !== null, onClick: accion }))} />
            </div>
            <h2 className="rv-ps-pase__titulo">{`${f.corto} a las ${f.hora}`}</h2>
            <strong className="rv-ps-proximo__que">{[p.title ?? '', etiqueta ?? ''].filter((x) => x !== '').join(' · ')}</strong>
            <span className="rv-ps-pase__cuando">{[paseNDeN(p), linea2].filter((x): x is string => x !== null && x !== '').join(' · ')}</span>
            <CambioDeFecha pase={p} />
            <BloqueQr qr={p.qr_url} codigo={p.code} etiqueta={`Código QR de ${p.holder_name ?? 'tu pase'}`} tamano={232} ampliable={false} />
            <BotonPc texto="Abrir el pase" icono="maximize-2" onClick={() => { navegador.ir(p.qr_url); }} />
            {ocupado && <p className="rv-ps-nota rv-ps-nota--info"><Icono nombre="loader-circle" className="rv-gira" /><span>Enviando…</span></p>}
            <NotaPase nota={nota} />
        </section>
    );
}

/** El icono del acceso en la cabecera del grupo (6.7): entrada, reserva o cita. */
function iconoDeAcceso(tipo: string | undefined): NombreIcono {
    if (tipo !== undefined && /cita|appointment/.test(tipo)) {
        return 'scissors';
    }
    if (tipo !== undefined && /reserva|booking/.test(tipo)) {
        return 'calendar-check';
    }
    return 'ticket';
}

function FilaPase({
    ops,
    perfil,
    pase: inicial,
    cabecera = null,
    enGrupo = false,
    abierto,
    onAbrir,
    onCaducada,
}: {
    ops: OperacionesClienteFinal;
    perfil: string;
    pase: MiPase;
    /** Primera fila de un acceso con varios pases: cuántos tiene (G785). */
    cabecera?: number | null;
    /** Dentro de un acceso con varios pases: la fila dice de quién es y «Pase n de N». */
    enGrupo?: boolean;
    abierto: boolean;
    onAbrir: () => void;
    onCaducada: () => void;
}) {
    const [detalle, setDetalle] = useState<MiPase | null>(null);
    const { ocupado, nota, setNota, reenviar } = useReenvio(ops, perfil, inicial.id, onCaducada);
    const { ocupada, anadir } = useCarteras(ops, setNota);

    // Al abrirlo, el pase al día (`getMyPass`): el QR y el estado de ahora.
    useEffect(() => {
        if (!abierto) {
            return;
        }
        let vivo = true;
        ops.getMyPass(perfil, inicial.id)
            .then((d) => {
                if (vivo) {
                    setDetalle(d);
                }
            })
            .catch((e: unknown) => {
                if (vivo && e instanceof ErrorApi && e.estado === 401) {
                    onCaducada();
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abierto, ops, perfil, inicial.id]);

    const p = abierto && detalle !== null ? detalle : inicial;
    const f = cuando(p);
    const etiqueta = etiquetaDe(p);
    const usable = sePuedeUsar(p.status);
    const chip = chipDelPase(p.status, fechaAnterior(p) !== null);
    const nDeN = paseNDeN(p);
    const partes = partesDelQr(p.qr_url);
    const id = `rv-ps-fila-${p.id}`;
    const datos: [string, string][] = [
        ['Cuándo', `${f.corto} · ${f.hora}`],
        ...(usable ? [['Puedes entrar', ventana(p)] as [string, string]] : []),
        ...(etiqueta === null ? [] : [['Tipo', etiqueta] as [string, string]]),
        ...(p.holder_name == null ? [] : [['A nombre de', p.holder_name] as [string, string]]),
        ...(p.arrived_at == null ? [] : [['Entraste', fechaHora(p.arrived_at, p.timezone)] as [string, string]]),
        ...(usable ? [] : [['Código', codigoEnGrupos(p.code)] as [string, string]]),
    ];
    const notaEstado: [NombreIcono, string] | null =
        p.status === 'voided' ? ['ban', 'Este pase está anulado y ya no vale para entrar.'] : p.status === 'expired' ? ['hourglass', 'No se usó y ya no vale.'] : p.status === 'inside' ? ['door-open', 'Ya se usó para entrar.'] : null;
    return (
        <div className={`rv-pc-pago${enGrupo && cabecera === null ? ' rv-ps-engrupo' : ''}`}>
            {cabecera !== null && (
                <div className="rv-ps-grupo">
                    <Icono nombre={iconoDeAcceso(p.passable_type)} />
                    <strong>{p.title ?? etiqueta ?? 'Pase'}</strong>
                    <span>{`· ${f.corto} · ${String(cabecera)} pases`}</span>
                </div>
            )}
            <button type="button" className="rv-pc-pago__cab" aria-expanded={abierto} aria-controls={id} onClick={onAbrir}>
                <span className="rv-ps-dia" aria-hidden="true">
                    <small>{f.corto.split(' ')[0]}</small>
                    <strong>{f.corto.split(' ')[1]}</strong>
                    <small>{f.corto.split(' ')[2]}</small>
                </span>
                <span>
                    <strong>{enGrupo ? (p.holder_name ?? p.title ?? etiqueta ?? 'Pase') : (p.title ?? etiqueta ?? 'Pase')}</strong>
                    <small>{[f.hora, etiqueta].filter((x): x is string => x !== null && x !== '').join(' · ')}</small>
                    <small>{(enGrupo ? [nDeN] : [nDeN, p.holder_name]).filter((x): x is string => x != null && x !== '').join(' · ')}</small>
                </span>
                <span className="rv-pc-pago__importe">{chip !== null && <Chip estado={chip[1]}>{chip[0]}</Chip>}</span>
                <Icono nombre="chevron-down" />
            </button>
            {abierto && (
                <div id={id} className="rv-pc-pago__cuerpo">
                    <CambioDeFecha pase={p} />
                    {usable && <BloqueQr qr={p.qr_url} codigo={p.code} etiqueta={`Código QR de ${p.holder_name ?? 'tu pase'}`} tamano={208} ampliable={false} />}
                    {datos.map(([k, v]) => (
                        <div key={k} className="rv-pc-dato"><span>{k}</span><strong>{v}</strong></div>
                    ))}
                    {notaEstado !== null && <p className="rv-pc-nota"><Icono nombre={notaEstado[0]} /><span>{notaEstado[1]}</span></p>}
                    {usable && (
                        <>
                            <BotonesCartera
                                apple={partes !== null}
                                google={partes !== null}
                                ocupada={ocupada}
                                onApple={() => { if (partes !== null) anadir('apple', partes.negocio, partes.token); }}
                                onGoogle={() => { if (partes !== null) anadir('google', partes.negocio, partes.token); }}
                            />
                            <div className="rv-pc-acciones">
                                <BotonPc texto="Abrir el pase" icono="maximize-2" onClick={() => { navegador.ir(p.qr_url); }} />
                                <BotonPc texto="Reenviármelo" icono="send" tipo="sec" ocupado={ocupado} ocupadoTexto="Enviando…" onClick={reenviar} />
                            </div>
                        </>
                    )}
                    <NotaPase nota={nota} />
                </div>
            )}
        </div>
    );
}
